/**
 * A single header action, rendered as one of §6's three pill variants.
 *
 * The prop keeps MUI's `contained | outlined | text` vocabulary rather than the
 * design's `primary | outline | ghost`: the two sets are the same three things,
 * and 18 call sites already speak the former. The pill shape and padding come
 * from the theme's `MuiButton` overrides, so every button in the app gets them,
 * not just the ones that happen to sit in a header.
 */
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';

export interface HeaderAction {
  label: string;
  to?: '/contests' | '/catalogue' | '/users' | '/contests/new' | '/configuration' | string;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  testId?: string;
  startIcon?: ReactNode;
}

export const HeaderActionButton = ({ action }: { action: HeaderAction }) => {
  const button = action.to ? (
    <Button
      component={Link}
      to={action.to}
      variant={action.variant ?? 'contained'}
      data-testid={action.testId}
      disabled={action.disabled}
      startIcon={action.startIcon}
    >
      {action.label}
    </Button>
  ) : (
    <Button
      onClick={action.onClick}
      variant={action.variant ?? 'contained'}
      data-testid={action.testId}
      disabled={action.disabled}
      startIcon={action.startIcon}
    >
      {action.label}
    </Button>
  );

  if (!action.disabled || !action.disabledReason) {
    return button;
  }

  const plainReason = typeof action.disabledReason === 'string' ? action.disabledReason : undefined;

  // A disabled button emits no pointer events, so the tooltip hangs off a live
  // wrapper — which is also the only element left that can carry the reason to
  // assistive tech and to a keyboard user who tabs onto it.
  //
  // No native `title` here: MUI treats one on a Tooltip's own child as an
  // error (it renders a second, duplicate tooltip), and it would be redundant
  // anyway — the Tooltip shows the reason, the wrapper announces it.
  //
  // The wrapper takes the button role rather than staying a bare span: ARIA
  // prohibits naming a role=generic element, so an aria-label here would be
  // dropped by real assistive tech. Carrying `aria-disabled` instead of
  // `disabled` is what keeps it focusable — a natively disabled control emits
  // no focus or pointer events, and then the tooltip could never open. The
  // inner button is hidden from the tree so the pair reads as one control.
  return (
    <Tooltip title={action.disabledReason}>
      <Box
        component="span"
        role="button"
        aria-disabled="true"
        tabIndex={0}
        aria-label={plainReason ? `${action.label}: ${plainReason}` : action.label}
        sx={{ display: 'inline-flex' }}
      >
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex' }}>
          {button}
        </Box>
      </Box>
    </Tooltip>
  );
};
